import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TrovataMessage, TrovataMessagePayload, TrovataMessageType, SessionIDMessage, MessageAction } from '../models/trovata-messaging.types';

@Injectable({
	providedIn: 'root',
})
export class MessagingService {
	private hostOriginSubject$: BehaviorSubject<string | null>;
	private incomingMessagesSubject$: BehaviorSubject<TrovataMessage | null>;
	private hostWindow: Window | null = null;
	hostOrigin$: Observable<string | null>;
	incomingMessages$: Observable<TrovataMessage | null>;

	constructor() {
		this.hostOriginSubject$ = new BehaviorSubject<string | null>(null);
		this.incomingMessagesSubject$ = new BehaviorSubject<TrovataMessage | null>(null);
		this.hostOrigin$ = this.hostOriginSubject$.asObservable();
		this.incomingMessages$ = this.incomingMessagesSubject$.asObservable();

		this.initializeMessageListener();
	}

	private initializeMessageListener(): void {
		window.addEventListener('message', this.handleMessage.bind(this));
	}

	private handleMessage(event: MessageEvent): void {
		this.saveOriginAndSource(event);
		// validate and forward incoming messages
		const validatedMessage: TrovataMessage | null = this.validateIncomingMessage(event.data);
		if (validatedMessage) {
			this.incomingMessagesSubject$.next(validatedMessage);
		}
	}

	private saveOriginAndSource(event: MessageEvent): void {
		// store the origin and source window from the initial session ID message
		if (!event.data || !event.data.type || event.data.type !== TrovataMessageType.SESSION_ID) {
			return;
		}
		// when running widget dev html file locally origin will be always 'null'
		if (!this.hostOriginSubject$.value && event.origin && event.origin !== 'null' && event.source) {
			this.hostOriginSubject$.next(event.origin);
			this.hostWindow = event.source as Window;
		}
	}

	private validateIncomingMessage(data: unknown): TrovataMessage | null {
		if (!data || typeof data !== 'object') {
			return null;
		}

		const message: Record<string, unknown> = data as Record<string, unknown>;

		if (!message['type'] || typeof message['type'] !== 'string' || !Object.values(TrovataMessageType).includes(message['type'] as TrovataMessageType)) {
			return null;
		}

		const validatedMessage: TrovataMessage = {
			type: message['type'] as TrovataMessageType,
			payload: message['payload'] as TrovataMessagePayload,
		};

		if (message['type'] === TrovataMessageType.SESSION_ID) {
			if (typeof message['sessionId'] === 'string') {
				(validatedMessage as SessionIDMessage).sessionId = message['sessionId'] as string;
			}

			if (typeof message['partnerId'] === 'string') {
				(validatedMessage as SessionIDMessage).partnerId = message['partnerId'] as string;
			}
		}

		return validatedMessage;
	}

	sendMessage(type: TrovataMessageType, payload?: TrovataMessagePayload): void {
		const hostOrigin: string | null = this.hostOriginSubject$.value;

		if (!hostOrigin || !this.hostWindow) {
			console.warn('Cannot send message: host origin or window reference not established');
			return;
		}

		const message: TrovataMessage = {
			type,
			payload,
		};

		this.hostWindow.postMessage(message, hostOrigin);
	}

	sendAction(action: MessageAction, data?: unknown): void {
		this.sendMessage(TrovataMessageType.ACTION, { action, data });
	}
}
